// meetingService.js
// -----------------------------------------------------------------------------
// API layer for the "Book a Meeting" feature. All endpoints are live:
//
//   getTeacherCourseOptions  GET    /students/google-meet/schedule-options/        (Mode A) - DEPRECATED
//   getAvailableSlots        GET    /students/google-meet/schedule-options/?...     (Mode B) - DEPRECATED
//   scheduleMeeting          POST   /students/google-meet/schedule/
//   getUpcomingMeetings      GET    /students/google-meet/schedule/?type=upcoming
//   getMeetingHistory        GET    /students/google-meet/schedule/?type=history
//   rescheduleMeeting        PUT    /students/google-meet/schedule/
//   cancelMeeting            POST   /students/meet/bookings/{booking_id}/cancel/
//
// (Google Calendar connect + save-credential live in googleCalendar.js.)
// -----------------------------------------------------------------------------

import api from "../../api/api"; // <-- adjust relative path to your axios instance

const authHeaders = (token) => ({
  "Content-Type": "application/json",
  Authorization: `Token ${token}`,
});

// Normalise every call to { ok, data, error }.
const safe = async (promise) => {
  try {
    const res = await promise;
    return { ok: true, data: res?.data ?? null, error: null };
  } catch (error) {
    const responseBody = error?.response?.data; // { status, message, errors } from the API
    const message =
      responseBody?.message ||
      responseBody?.detail ||
      error?.message ||
      "Something went wrong. Please try again.";
    return { ok: false, data: responseBody ?? null, error: message };
  }
};

// Unwrap the backend's { status, message, data } envelope. Treats a body-level
// status >= 400 as an error even when the HTTP status was 200.
const unwrap = (r, pick) => {
  if (!r.ok) return r;
  const body = r.data || {};
  if (body.status && Number(body.status) >= 400) {
    return { ok: false, data: null, error: body.message || "Request failed." };
  }
  return {
    ok: true,
    error: null,
    data: pick ? pick(body) : body.data,
    raw: body,
  };
};

// -----------------------------------------------------------------------------
// REAL — Schedule flow
// -----------------------------------------------------------------------------

// Mode A: teacher + course pairs (one row per teacher-course).
// data: [{ teacher_id, teacher_name, course_id, course_name }]
export const getTeacherCourseOptions = async (token) => {
  const r = await safe(
    api.get("/students/google-meet/schedule-options/", {
      headers: authHeaders(token),
    })
  );
  return unwrap(r, (b) => b.data || []);
};

// Mode B: time slots for a teacher on a date.
// data: [{ time, label, is_full }]  (+ timezone, teacher_timezone on the result)
export const getAvailableSlots = async (token, { teacherId, date, timezone }) => {
  const params = { teacher_id: teacherId, date };
  if (timezone) params.timezone = timezone;
  const r = await safe(
    api.get("/students/google-meet/schedule-options/", {
      headers: authHeaders(token),
      params,
    })
  );
  const out = unwrap(r, (b) => b.data || []);
  if (out.ok) {
    out.timezone = out.raw?.timezone;
    out.teacher_timezone = out.raw?.teacher_timezone;
  }
  return out;
};

// POST schedule.
// payload: { teacher_id, course_id, date: "YYYY-MM-DD", time: "HH:mm", timezone }
// returns the created meeting object (id, meet_link, start/end_time, ...).
export const scheduleMeeting = async (token, payload) => {
  const r = await safe(
    api.post("/students/google-meet/schedule/", payload, {
      headers: authHeaders(token),
    })
  );
  const out = unwrap(r, (b) => b.data || null);
  if (out.ok) out.message = out.raw?.message;
  return out;
};

// -----------------------------------------------------------------------------
// REAL — Upcoming & History (same endpoint, distinguished by ?type=)
// -----------------------------------------------------------------------------

// Normalise a schedule row to the shape the UI components read.
const normalizeMeeting = (m) => ({
  ...m, // keeps meet_link, calendar_link, course_id, status, etc.
  time: m.start_time, // components use `time`
  join_url: m.meet_link, // "Join Meeting" button + Copy link
  // (calendar_link is used by the "Open in Google Calendar" icon)
});

export const getUpcomingMeetings = async (token) => {
  const r = await safe(
    api.get("/students/meet/bookings/", {
      headers: authHeaders(token),
      params: { type: "upcoming" },
    })
  );
  const result = unwrap(r, (b) => {
    // Handle both array and object responses
    let meetings = b.data || [];
    if (typeof meetings === 'object' && !Array.isArray(meetings)) {
      // If data is an object, check if it has a bookings array
      meetings = meetings.bookings || meetings.meetings || [];
    }
    if (!Array.isArray(meetings)) {
      console.warn("Expected array, got:", meetings);
      return [];
    }
    return meetings.map(normalizeMeeting);
  });
  return result;
};

export const getMeetingHistory = async (token) => {
  const r = await safe(
    api.get("/students/meet/bookings/", {
      headers: authHeaders(token),
      params: { type: "history" },
    })
  );
  const result = unwrap(r, (b) => {
    // Handle both array and object responses
    let meetings = b.data || [];
    if (typeof meetings === 'object' && !Array.isArray(meetings)) {
      // If data is an object, check if it has a bookings array
      meetings = meetings.bookings || meetings.meetings || [];
    }
    if (!Array.isArray(meetings)) {
      console.warn("Expected array, got:", meetings);
      return [];
    }
    return meetings.map(normalizeMeeting);
  });
  return result;
};

// -----------------------------------------------------------------------------
// REAL — Reschedule (PUT) & Cancel (DELETE), same /schedule/ endpoint
// -----------------------------------------------------------------------------

// PUT body: { meeting_id, date, time, timezone }. Returns the updated meeting.
export const rescheduleMeeting = async (token, meetingId, { date, time, timezone }) => {
  const r = await safe(
    api.put(
      "/students/google-meet/schedule/",
      { meeting_id: meetingId, date, time, timezone },
      { headers: authHeaders(token) }
    )
  );
  const out = unwrap(r, (b) => b.data || null);
  if (out.ok) out.message = out.raw?.message;
  return out;
};

// DELETE body: { meeting_id }. (axios sends a DELETE body via `data`.)
export const cancelMeeting = async (token, meetingId) => {
  const r = await safe(
    api.post(`/students/meet/bookings/${meetingId}/cancel/`, {}, {
      headers: authHeaders(token),
    })
  );
  const out = unwrap(r, () => null);
  if (out.ok) out.message = out.raw?.message;
  return out;
};

// Get teacher booking options (student side) or own options (teacher side reschedule)
// GET /students/meet/options/ (teacher side - no params)
// GET /students/meet/options/?teacher_id=T00123 (student side)
// GET /students/meet/options/?teacher_id=T00123&for_reschedule=booking_id (student suggesting time in reschedule negotiation)
export const getTeacherOptions = async (token, teacherId, forReschedule) => {
  const params = {};
  if (teacherId) params.teacher_id = teacherId;
  if (forReschedule) params.for_reschedule = forReschedule;

  const r = await safe(
    api.get("/students/meet/options/", {
      headers: authHeaders(token),
      params,
    })
  );
  return unwrap(r, (b) => b.data ?? null);
};

// Get available time slots (student booking or teacher reschedule)
// GET /students/meet/slots/?teacher_id=T00123&date=2026-08-20&session_slot=30min&timezone=... (student side)
// GET /students/meet/slots/?date=2026-08-20&timezone=...&for_reschedule=42 (teacher side reschedule)
export const getBookingSlots = async (token, { teacherId, date, sessionSlot, for_reschedule }) => {
  const params = { date };
  if (teacherId) params.teacher_id = teacherId;
  if (sessionSlot) params.session_slot = sessionSlot;
  if (for_reschedule) params.for_reschedule = for_reschedule;
  const r = await safe(
    api.get("/students/meet/slots/", {
      headers: authHeaders(token),
      params,
    })
  );
  return unwrap(r, (b) => b.data ?? null);
};

// Create a meeting booking (called after payment is successful)
// POST /students/meet/bookings/
// payload: { teacher_id, course_id, offering_id, date, time, timezone }
// returns: { message, data: { payment_required, booking: { status, status_label, meet_link, ... } } }
export const createMeetingBooking = async (token, payload) => {
  const r = await safe(
    api.post("/students/meet/bookings/", payload, {
      headers: authHeaders(token),
    })
  );
  const out = unwrap(r, (b) => b.data ?? null);
  if (out.ok) out.message = out.raw?.message;
  return out;
};

// Teacher availability toggle (teacher-only "Enable Calendar Meetings").
// PUT body: { teacher_id, is_available }
export const setTeacherAvailability = async (token, teacherId, isAvailable) => {
  const r = await safe(
    api.put(
      "/students/google-meet/teacher-availability/",
      { teacher_id: teacherId, is_available: isAvailable },
      { headers: authHeaders(token) }
    )
  );
  const out = unwrap(r, (b) => b.data ?? null);
  if (out.ok) out.message = out.raw?.message;
  return out;
};

// Get teacher meeting settings (meeting enabled, free trial availability).
// GET /students/meet/teacher/settings/
// Returns: { is_meeting_enabled, is_free_trial_available }
export const getMeetingSettings = async (token) => {
  const r = await safe(
    api.get("/students/meet/teacher/settings/", {
      headers: authHeaders(token),
    })
  );
  return unwrap(r, (b) => b.data ?? {});
};

// Update teacher meeting settings.
// PUT /students/meet/teacher/settings/
// payload: { is_meeting_enabled, is_free_trial_available }
export const updateMeetingSettings = async (token, payload) => {
  const r = await safe(
    api.put("/students/meet/teacher/settings/", payload, {
      headers: authHeaders(token),
    })
  );
  const out = unwrap(r, (b) => b.data ?? null);
  if (out.ok) out.message = out.raw?.message;
  return out;
};

// ============================================================================
// 1-on-1 Session Setup Card (complete flow)
// ============================================================================

// GET /students/meet/teacher/setup/
// Returns: settings, account, google_connected, offerings, availability
export const getTeacherSetup = async (token) => {
  const r = await safe(
    api.get("/students/meet/teacher/setup/", {
      headers: authHeaders(token),
    })
  );
  return unwrap(r, (b) => b.data ?? {});
};

// PUT /students/meet/teacher/setup/
// Save entire setup card in one transaction.
// payload: { settings?, offerings?, availability? }
// Returns: { settings, account, offerings, availability, changes, message }
export const saveTeacherSetup = async (token, payload) => {
  const r = await safe(
    api.put("/students/meet/teacher/setup/", payload, {
      headers: authHeaders(token),
    })
  );
  const out = unwrap(r, (b) => b.data ?? null);
  if (out.ok) {
    out.message = out.raw?.message;
    out.changes = out.raw?.changes;
  }
  return out;
};

// POST /students/meet/bookings/<booking_id>/reschedule-request/
// Teacher: request to reschedule a meeting
// payload: { start (ISO string with timezone), offering_id, reason }
// start: "2026-08-31T15:00:00-04:00" (unmodified from API, not date+time)
export const requestRescheduleMeeting = async (token, bookingId, payload) => {
  const r = await safe(
    api.post(`/students/meet/bookings/${bookingId}/reschedule-request/`, payload, {
      headers: authHeaders(token),
    })
  );
  const out = unwrap(r, (b) => b.data ?? null);
  if (out.ok) out.message = out.raw?.message;
  return out;
};

// POST /students/meet/reschedule-requests/<request_id>/respond/
// Student: respond to a reschedule request from teacher
// payload: { action: "accept" or "decline", note?: "..." }
export const respondToRescheduleRequest = async (token, requestId, payload) => {
  const r = await safe(
    api.post(`/students/meet/reschedule-requests/${requestId}/respond/`, payload, {
      headers: authHeaders(token),
    })
  );
  const out = unwrap(r, (b) => b.data ?? null);
  if (out.ok) out.message = out.raw?.message;
  return out;
};
